import React, { useState, useEffect } from 'react';
import CargomaticInput from 'components/v2/input';
import Button from 'components/v2/buttons/Button';
import { useTranslation } from 'react-i18next';
import { humanize, titleize } from 'underscore.string';
import DatePicker from 'components/v2/DatePicker';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import moment from 'moment';
import CargomaticModal from 'components/v2/modal';
import Label from 'components/v2/forms/Label';
import { sluggify } from 'utils/localization/utils';
import PropTypes from 'prop-types';
/* Add whatever additional date needed to the whitelist */
const DATE_WHITE_LIST = [
  'last_free_date',
  'arrival_date',
  'last_cut_date',
  'dispatch_date',
  'discharge_date',
  'earliest_empty_date',
  'empty_returned_date',
];

function getMomentDate(date) {
  if (!date) {
    return null;
  }
  return moment(date);
}

export default function ShipmentDateModal({
  field,
  currentFieldValue = '',
  shipment,
  onSuccess,
  onFail,
  onHide,
  locale,
  show,
}) {
  const [selectedDateField, setSelectedDateField] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    setInitialData();
  }, [field, show]);

  const setInitialData = () => {
    if (currentFieldValue) {
      setSelectedDate(getMomentDate(currentFieldValue));
    }
    setSelectedDateField(field);
    setShowModal(true);
  };

  const getSelectedDateLabel = () => {
    return titleize(humanize(selectedDateField || ''));
  };

  const getDateModalTitle = () => {
    return getSelectedDateLabel();
  };

  const handleHideModal = () => {
    onCancelDateSave();
    onHide();
  };

  const handleDateChange = (date) => {
    const momentDate = getMomentDate(date);
    setSelectedDate(momentDate);
  };

  const verifyDateField = () => {
    return DATE_WHITE_LIST.includes(field.toLowerCase());
  };

  const onAddDateSuccess = () => {
    setIsSaving(false);
    setShowModal(false);
    onSuccess();
  };

  const onAddDateFail = (e) => {
    setIsSaving(false);
    onFail(e);
  };

  const isValidDate = () => {
    return selectedDate && getMomentDate(selectedDate).isValid();
  };

  const onAddDateClick = async () => {
    try {
      setIsSaving(true);
      await saveShipmentDate();
      onAddDateSuccess();
    } catch (e) {
      console.log('Failed to add shipment date', e.message);
      onAddDateFail(e);
    }
  };

  const onCancelDateSave = () => {
    setSelectedDate('');
    setSelectedDateField('');
    onHide();
  };

  const saveShipmentDate = () => {
    return ShipmentsActionCreators.updateShipmentActionDates(
      shipment,
      selectedDateField,
      selectedDate,
    );
  };

  const getLabel = () => {
    return humanize(t(sluggify(field)));
  };

  const isDisabled = () => {
    return isSaving || !isValidDate();
  };

  const renderFooter = () => (
    <div
      data-testid="div-fd40ecb8"
      className="form-actions flex justify-end space-x-4 mt-auto">
      <Button
        dataTestId="Button-2ac3f5bb"
        color="green"
        disabled={isDisabled()}
        onClick={() => onAddDateClick()}
        className="block mr-2 button button-green">
        {t('ADD_DATE')}
      </Button>
      <Button
        dataTestId="Button-f8b122b3"
        disabled={isSaving}
        onClick={() => onCancelDateSave()}
        className="block button secondary-action">
        {t('CANCEL')}
      </Button>
    </div>
  );

  const renderBody = () => {
    return (
      (<div data-testid="div-1b4ff37e" className="grid grid-cols-2 gap-6">
        <div data-testid="div-a05ada66">
          <Label dataTestId="Label-afd16b60" className="mb-2">{getLabel()}</Label>
          <DatePicker
            dataTestId="DatePicker-b72f9de8"
            key={0}
            selected={selectedDate}
            className="block"
            locale={locale}
            onChange={(value) => handleDateChange(value)}
            isClearable />
        </div>
      </div>)
    );
  };

  const label = `${t('PROVIDE')} ${getSelectedDateLabel()}`;

  if (!selectedDateField) {
    return null;
  }

  if (!verifyDateField()) {
    return null;
  }
  return (
    (<CargomaticModal
      dataTestId="CargomaticModal-510fdb09"
      show={!!showModal}
      style={{ top: '25%' }}
      title={label}
      onHide={() => handleHideModal()}
      body={renderBody()}
      footer={renderFooter()} />)
  );
}

ShipmentDateModal.propTypes = {
  field: PropTypes.string.isRequired,
  currentFieldValue: PropTypes.string,
  shipment: PropTypes.object.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onFail: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
  locale: PropTypes.string,
  show: PropTypes.bool.isRequired,
};
